/**
 * Dashboard de Learning (doc de Andrea, ajustes 14/09 §2): tres pestañas para
 * las dos vistas. People (rol people/admin) ve toda la nómina activa —
 * Comparativa / New Clappers / Old Clappers — y, si además tiene equipo, puede
 * alternar a la vista Líder (Mi equipo / New / Old, solo su gente). Un líder
 * sin rol People solo tiene la vista Líder. El scope lo resuelve el server.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { ComparativaTab } from './ComparativaTab';
import { MiEquipoTab } from './MiEquipoTab';
import { ClappersTab } from './ClappersTab';
import { UserDetailDialog } from './UserDetailDialog';
import { todayAR } from '@/../../shared/dates';

type Role = 'people' | 'leader';
type Tab = 'summary' | 'new' | 'old';

export function LearningDashboard({ scope, hasTeam, canEdit }: { scope: 'all' | 'team'; hasTeam: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const k = 'people_learning.dash';
  const [role, setRole] = useState<Role>(scope === 'all' ? 'people' : 'leader');
  const [tab, setTab] = useState<Tab>('summary');
  const [detailEmail, setDetailEmail] = useState<string | null>(null);
  const view = role === 'people' ? 'all' : 'team';
  const { data, isLoading, error } = trpc.peopleLearning.dashboard.useQuery({ view }, { staleTime: 30_000, retry: false });
  const today = todayAR();

  if (isLoading) return <div className="flex justify-center py-16"><WinclapLoader /></div>;
  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t(`${k}.no_access`, 'Sin acceso al dashboard')}</AlertTitle>
        <AlertDescription>{error?.message}</AlertDescription>
      </Alert>
    );
  }
  const rows = data.rows;
  const isPeople = role === 'people';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="summary">{isPeople ? t(`${k}.tab_comparativa`, 'Comparativa') : t(`${k}.tab_team`, 'Mi equipo')}</TabsTrigger>
            <TabsTrigger value="new">{t(`${k}.tab_new`, 'New Clappers')} <span className="ml-1 text-xs font-normal text-muted-foreground">({t(`${k}.mandatory`, 'Mandatory')})</span></TabsTrigger>
            <TabsTrigger value="old">{t(`${k}.tab_old`, 'Old Clappers')} <span className="ml-1 text-xs font-normal text-muted-foreground">({t(`${k}.optional`, 'Optional')})</span></TabsTrigger>
          </TabsList>
        </Tabs>
        {scope === 'all' && hasTeam && (
          <div className="text-right">
            <div className="inline-flex rounded-lg border bg-background p-0.5">
              {(['people', 'leader'] as Role[]).map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={cn('rounded-md px-3 py-1.5 text-xs font-semibold transition-colors', role === r ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground')}
                >
                  {r === 'people' ? t(`${k}.view_people`, 'Vista People') : t(`${k}.view_leader`, 'Vista Líder')}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {isPeople ? t(`${k}.view_people_note`, 'Toda la organización') : t(`${k}.view_leader_note`, 'Solo las personas a tu cargo')}
            </p>
          </div>
        )}
      </div>

      {tab === 'summary' && (isPeople
        ? <ComparativaTab rows={rows} launchDate={data.launchDate} today={today} onDetail={setDetailEmail} />
        : <MiEquipoTab rows={rows} today={today} onDetail={setDetailEmail} />)}
      {/* key por vista: al cambiar People ⇄ Líder los filtros arrancan de cero (el select de Líder desaparece en la vista Líder) */}
      {tab === 'new' && <ClappersTab key={`new-${view}`} rows={rows} mode="new" scope={view} launchDate={data.launchDate} today={today} onDetail={setDetailEmail} />}
      {tab === 'old' && <ClappersTab key={`old-${view}`} rows={rows} mode="old" scope={view} launchDate={data.launchDate} today={today} onDetail={setDetailEmail} />}

      {detailEmail && (
        <UserDetailDialog
          email={detailEmail}
          row={rows.find((r) => r.email === detailEmail) ?? null}
          canEdit={canEdit}
          today={today}
          onClose={() => setDetailEmail(null)}
        />
      )}
    </div>
  );
}
